import React, { startTransition, useEffect, useId, useRef, useState } from "react";
import {
  Dialog,
  Stack,
  Box,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { alpha } from "@mui/material";

const FullscreenDialog = ({
  open,
  onClose,
  title,
  subtitle,
  addButtonInHeader,
  showAddButton,
  addButtonLabel,
  onAdd,
  children,
}) => {
  const [contentReady, setContentReady] = useState(false);
  const frameRef = useRef(null);
  const titleId = useId();
  const showContent = open && contentReady;

  useEffect(() => {
    if (!open) setContentReady(false);

    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [open]);

  const handleEntered = () => {
    if (!open) return;
    // Paint the shell and loading feedback before mounting expensive tables.
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        startTransition(() => setContentReady(true));
      });
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      aria-labelledby={titleId}
      TransitionProps={{ onEntered: handleEntered }}
      PaperProps={{
        sx: { bgcolor: "background.default", overflow: "hidden" },
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        spacing={2}
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
          flexShrink: 0,
        }}
      >
        <Box flex={1}>
          <Typography
            id={titleId}
            variant='h6'
            fontWeight={600}
            color='text.primary'
          >
            {title || "Fullscreen view"}
          </Typography>
          {subtitle && (
            <Typography variant='body2' color='text.secondary'>
              {subtitle}
            </Typography>
          )}
        </Box>
        {/* Add Row Button in Fullscreen Header if needed */}
        {addButtonInHeader && showAddButton && (
          <Tooltip title={addButtonLabel}>
            <span>
              <IconButton
                color='primary'
                disabled={!showContent}
                aria-label={addButtonLabel || "Add row"}
                onClick={() => {
                  onAdd && onAdd();
                }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title='Exit fullscreen'>
          <IconButton
            color='primary'
            onClick={onClose}
            aria-label='Exit fullscreen'
          >
            <FullscreenExitIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        aria-busy={!showContent}
        sx={{ p: 1, flex: 1, minHeight: 0, overflow: "auto" }}
      >
        {showContent ? (
          typeof children === "function" ? children() : children
        ) : (
          <Stack
            role='status'
            aria-live='polite'
            spacing={1.5}
            alignItems='center'
            justifyContent='center'
            sx={{ minHeight: "100%", px: 3, py: 4, textAlign: "center" }}
          >
            <CircularProgress
              size={32}
              disableShrink
              aria-label='Loading fullscreen content'
            />
            <Typography variant='subtitle1' fontWeight={600}>
              Opening fullscreen...
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Preparing content. Please wait.
            </Typography>
          </Stack>
        )}
      </Box>
    </Dialog>
  );
};

export default FullscreenDialog;
